
import React from "react";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Lead } from "@/context/LeadContext";
import LeadStatusBadge from "../ui-custom/LeadStatusBadge";
import ServiceBadge from "../ui-custom/ServiceBadge";
import { Button } from "@/components/ui/button";
import {
  MessageSquare, Eye, UserRound, Phone, MapPin, Clock,
  Megaphone, CheckCircle2, XCircle, Globe, Share2, Flame,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import LeadDuplicateHistory from "./LeadDuplicateHistory";

const SOURCE_META: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
  facebook_api: {
    label: "Meta API · Realtime",
    icon: <Share2 className="h-3 w-3" />,
    className: "bg-violet-50 text-violet-700 border-violet-200",
  },
  facebook_leads: {
    label: "Facebook Ads",
    icon: <Share2 className="h-3 w-3" />,
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  facebook_sheets: {
    label: "Facebook Sheets",
    icon: <Share2 className="h-3 w-3" />,
    className: "bg-indigo-50 text-indigo-700 border-indigo-200",
  },
  web: {
    label: "Web",
    icon: <Globe className="h-3 w-3" />,
    className: "bg-slate-50 text-slate-600 border-slate-200",
  },
};

interface LeadCardProps {
  lead: Lead;
  onSendSMS?: (leadId: string) => void;
  children?: React.ReactNode;
  isDuplicate?: boolean;
}

function safeLeadDate(d: string): Date {
  const iso = d.includes("T") ? d : d.replace(" ", "T");
  const utc = iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + "Z";
  return new Date(utc);
}

function getAgingInfo(lead: Lead): { days: number; level: "warn" | "danger" | null } {
  const activeStages = ["Νέο", "σεΕκκρεμότητα"];
  if (!activeStages.includes(lead.stage)) return { days: 0, level: null };
  const days = Math.floor((Date.now() - safeLeadDate(lead.createdAt).getTime()) / 86_400_000);
  if (days >= 5) return { days, level: "danger" };
  if (days >= 2) return { days, level: "warn" };
  return { days, level: null };
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onSendSMS, children, isDuplicate }) => {
  const timeAgo = formatDistanceToNow(new Date(lead.createdAt), {
    addSuffix: true,
    locale: el,
  });

  const sourceMeta = lead.source ? (SOURCE_META[lead.source] ?? null) : null;
  const aging = getAgingInfo(lead);

  // Strip "Location based on " prefix if present
  const cleanAddress = lead.address?.replace(/^location based on\s*/i, "") || "";

  const getStageColor = () => {
    switch (lead.stage) {
      case "Νέο":              return "border-l-blue-500";
      case "σεΕκκρεμότητα":   return "border-l-amber-500";
      case "έκλεισεΗΔουλειά": return "border-l-emerald-500";
      case "ήθελεΠληροφορίες":return "border-l-slate-400";
      default:                  return "border-l-slate-300";
    }
  };

  const SmsIcon = () => {
    if (!lead.smsStatus) return null;
    if (lead.smsStatus === "sent" || lead.smsStatus === "delivered") {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
          </TooltipTrigger>
          <TooltipContent>SMS εστάλη</TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <XCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" />
        </TooltipTrigger>
        <TooltipContent>SMS απέτυχε</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
      <Card className={cn(
        "group rounded-2xl border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200",
        "border-l-4",
        getStageColor()
      )}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex gap-3">
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-sm font-semibold text-slate-600">
                {lead.name.charAt(0).toUpperCase()}
              </span>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Row 1: Name + Stage + Actions */}
              <div className="flex flex-col items-start gap-2 sm:flex-row sm:justify-between">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <h3 className="max-w-[180px] truncate font-semibold text-slate-900 sm:max-w-[280px]">{lead.name}</h3>
                  <LeadStatusBadge status={lead.stage} />
                </div>

                {/* Actions become a full-width row on mobile so names and badges never collide. */}
                <div className="flex w-full shrink-0 items-center justify-end gap-1.5 sm:w-auto">
                  {children ? children : (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onSendSMS && onSendSMS(lead.id)}
                            disabled={!lead.partnerId}
                            className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Αποστολή SMS</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to={`/lead/${lead.id}`}>
                            <Button
                              size="sm"
                              className="h-8 gap-1.5 bg-slate-900 hover:bg-slate-800 text-xs px-3"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Προβολή
                            </Button>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent>Λεπτομέρειες επαφής</TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>

              {/* Row 2: Time + Service + Source + Campaign + Aging + Duplicate */}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="h-3 w-3" />
                  {timeAgo}
                </span>
                <ServiceBadge service={lead.service} />
                {aging.level === "danger" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 animate-pulse cursor-default">
                        <Flame className="h-3 w-3" />
                        {aging.days} μέρες
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Αναπάντητο εδώ και {aging.days} μέρες — χρειάζεται άμεση ενέργεια</TooltipContent>
                  </Tooltip>
                )}
                {aging.level === "warn" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 cursor-default">
                        <Clock className="h-3 w-3" />
                        {aging.days} μέρες
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Αναπάντητο εδώ και {aging.days} μέρες</TooltipContent>
                  </Tooltip>
                )}
                {isDuplicate && (
                  <LeadDuplicateHistory leadId={lead.id} name={lead.name} />
                )}
                {sourceMeta && (
                  <span className={cn("flex items-center gap-1 text-xs border rounded-full px-2 py-0.5", sourceMeta.className)}>
                    {sourceMeta.icon}
                    {sourceMeta.label}
                  </span>
                )}
                {lead.campaign && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 max-w-[200px] truncate cursor-default">
                        <Megaphone className="h-3 w-3 shrink-0" />
                        {lead.campaign}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{lead.campaign}</TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Row 3: Phone + Address + Partner */}
              <div className="mt-2 flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-5">
                <a
                  href={`tel:${lead.phone}`}
                  className="flex min-h-10 items-center gap-1.5 text-sm text-slate-600 transition-colors hover:text-blue-600"
                >
                  <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  {lead.phone}
                </a>

                {cleanAddress && (
                  <span className="flex min-h-8 items-center gap-1.5 text-sm text-slate-500">
                    <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate max-w-[220px]">{cleanAddress}</span>
                    {lead.postalCode && (
                      <span className="text-xs text-slate-400">{lead.postalCode}</span>
                    )}
                  </span>
                )}

                <span className="flex min-h-8 items-center gap-1.5 text-sm">
                  <UserRound className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  {lead.partnerName ? (
                    <span className="text-slate-700 font-medium truncate max-w-[160px]">{lead.partnerName}</span>
                  ) : (
                    <span className="text-amber-600 italic">Χωρίς ανάθεση</span>
                  )}
                  <SmsIcon />
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default LeadCard;
